import { FetchResult } from '@apollo/client';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ConversationParticipant, CustomAttributeDefinition } from '@provision/entity-types';
import { useCallback, useMemo } from 'react';
import { AddClientModal } from 'src/components/AddClientModal/add-client-modal';
import { AddComplimentaryPeriodModal } from 'src/components/AddComplimentaryPeriodModal/add-complimentary-period-modal';
import { AddConversationModal } from 'src/components/AddConversationModal/add-conversation-modal';
import { AddPartnerDomainModal } from 'src/components/AddPartnerDomainModal/add-partner-domain-modal';
import { AddPreSoldMembershipFormValues } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-form';
import { AddPreSoldMembershipModal } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-modal';
import { AddProjectModal } from 'src/components/AddProjectModal/add-project-modal';
import { AddProjectFormValues } from 'src/components/AddProjectModal/use-add-project-form';
import { AddressFileRenameModal } from 'src/components/AddressFileRenameModal/address-file-rename-modal';
import { AddSiteModal } from 'src/components/AddSiteModal/add-site-modal';
import { SupportPolicyFormValues } from 'src/components/Assistant/policies-tab/policy-form';
import { SupportPolicyModal } from 'src/components/Assistant/policies-tab/policy-modal';
import { ClientSegmentAddModal } from 'src/components/ClientSegmentModals/client-segment-add-modal';
import { ClientSegmentEditModal } from 'src/components/ClientSegmentModals/client-segment-edit-modal';
import { ContactSupportModal } from 'src/components/ContactSupportModal/contact-support-modal';
import { CopyTextModal } from 'src/components/CopyTextModal/copy-text-modal';
import { CreatePartnerModal } from 'src/components/CreatePartnerModal/create-partner-modal';
import { CreateUserModal } from 'src/components/CreateUserModal/create-user-modal';
import { CustomSignupLinkModal } from 'src/components/CustomSignupLinkModal/custom-signup-link-modal';
import { ImportDtoolsProjectModal } from 'src/components/ImportDtoolsProjectModal/import-dtools-project-modal';
import { ImportPortalioProjectModal } from 'src/components/ImportPortalioProjectModal/import-portalio-project-modal';
import { ImportProposalsModal } from 'src/components/ImportProposalsModal/import-proposals-modal';
import { MemberServicesFollowUpModal } from 'src/components/MemberServicesFollowUpModal/member-services-followup-modal';
import { MergeClientModal } from 'src/components/MergeClientModal/merge-client-modal';
import { MergeConversationModal } from 'src/components/MergeConversationModal/merge-conversation-modal';
import { MergeSiteModal } from 'src/components/MergeSiteModal/merge-site-modal';
import { PauseSubscriptionModal } from 'src/components/PauseSubscriptionModal/pause-subscription-modal';
import { ProposalLostModal } from 'src/components/ProposalLostModal/proposal-lost-modal';
import { ProposalWonModal } from 'src/components/ProposalWonModal/proposal-won-modal';
import {
  PublicApiKeyModal,
  PublicApiKeyModalProps,
} from 'src/components/PublicApiKeyModal/public-api-key-modal';
import { QuickAction } from 'src/components/QuickActions/quick-actions-context';
import { QuickActionsModal } from 'src/components/QuickActions/quick-actions-modal';
import { ResentInviteWithPasswordModal } from 'src/components/ResendInviteWithPasswordModal/resend-invite-with-password-modal';
import { ScheduleHolidayModal } from 'src/components/ScheduleHolidayModal/schedule-holiday-modal';
import { SendMembershipEmailModal } from 'src/components/SendMembershipEmailModal/send-membership-email-modal';
import { ShortcutsModal } from 'src/components/ShortcutsModal/shortcuts-modal';
import { SnoozeConversationModal } from 'src/components/SnoozeConversationModal/snooze-conversation-modal';
import { SubmitDay1Modal } from 'src/components/SubmitDay1Modal/submit-day1-modal';
import { UpdateClientToAddressModal } from 'src/components/UpdateClientToAddressModal/update-client-to-address-modal';
import { UpdateConversationToAddressModal } from 'src/components/UpdateConversationToAddressModal/update-conversation-to-address-modal';
import { UpdateUserAssigneeGroupsModal } from 'src/components/UpdateUserAssigneeGroups/update-user-assignee-groups-modal';
import { UpdateUserFormFields } from 'src/components/UpdateUserModal/types';
import { UpdateUserModal } from 'src/components/UpdateUserModal/update-user-modal';
import { UpdateUserPermissionsModal } from 'src/components/UpdateUserPermissionsModal/update-user-permissions-modal';
import { UserSearchModal } from 'src/components/UserSearchModal/user-search-modal';

import {
  Address,
  Client,
  Macro,
  ManageUserConversationAssigneeGroups,
  MergeClient,
  PreparedConversationParticipantOption,
  Project,
} from 'src/graphql';
import type { BrandWithSupport } from 'src/graphql/entities/brand';
import { Conversation } from 'src/graphql/entities/conversation/conversation.types';
import type { ConversationAssigneeGroupManagement } from 'src/graphql/entities/conversation-assignee-group';
import { CustomAttribute } from 'src/graphql/entities/custom-attributes';
import { PartnerHolidayEntity } from 'src/graphql/entities/partner-holiday/partner-holiday.types';
import {
  CreateStripeSubscriptionScheduleMutation,
  ScheduleCreationParams,
} from 'src/graphql/entities/stripe-subscription-schedule';
import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { ManageUserToUserGroupParams } from 'src/graphql/entities/user-permissions';
import {
  ConditionInput,
  EntityNames,
  NullableDeepPartial,
  SortingInput,
} from 'src/graphql/types';
import { AiPromptModal } from 'src/pages/Conversations/Chat/AiAssistant/ai-prompts-modal';
import { MacrosModal } from 'src/pages/Conversations/Chat/Macros/macros-modal';
import { AddCustomAttributeModal } from 'src/pages/Settings/custom-attributes/add-custom-attribute-modal';
import { ManageCustomAttributeGroupModal } from 'src/pages/Settings/custom-attributes/manage-custom-attribute-group-modal';
import {
  SignalOnCallScheduleModal,
  SignalOnCallScheduleModalProps,
} from 'src/pages/Settings/signal-oncall-schedule/signal-oncall-schedule-modal';
import { ManageAssigneeGroupModal } from 'src/pages/Settings/users/assignee-groups/manage-assignee-group-modal';
import { CreateDraftWorkflowVersionModal } from 'src/pages/Settings/workflows/components/create-draft-version-modal';
import { ClientRaw, PlatformUserRaw, WorkflowRaw } from 'src/types';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export enum Modals {
  SnoozeConversationModal = 'snoozeConversation',
  MergeConversationModal = 'mergeConversation',
  MergeSiteModal = 'mergeSite',
  MergeClientModal = 'mergeClient',
  AddSiteModal = 'addSite',
  AddClientModal = 'addClient',
  AddConversationModal = 'addConversation',
  AddCustomAttributeModal = 'addCustomAttribute',
  ManageCustomAttributGroupModal = 'manageCustomAttributeGroup',
  ContactSupportModal = 'contactSupport',
  MemberServicesFollowUp = 'memberServicesFollowUp',
  SendMembershipEmail = 'sendMembershipEmail',
  AddComplimentaryPeriod = 'addComplimentaryPeriod',
  AddPreSoldMembership = 'addPreSoldMembership',
  AddPartnerDomainModal = 'addPartnerDomain',
  CreatePartnerModal = 'createPartnerModal',
  UpdateUser = 'updateUser',
  UpdateUserPermissions = 'updateUserPermissions',
  AddProject = 'addProject',
  CreateUser = 'createUser',
  SubmitDay1 = 'submitDay1',
  ResentInviteWithPassword = 'resendInviteWithPassword',
  ProposalWon = 'proposalWon',
  ProposalLost = 'proposalLost',
  QuickActionsModal = 'quickActionsModal',
  MacrosModal = 'macrosModal',
  UserSearchModal = 'userSearchModal',
  UpdateAssigneeGroups = 'updateAssigneeGroups',
  ScheduleHolidays = 'scheduleHolidays',
  UpdateConversationToAddress = 'updateConversationToAddress',
  UpdateClientToAddress = 'updateClientToAddress',
  AiPrompts = 'aiPrompts',
  ShortcutsModal = 'shortcutsModal',
  AddressFileRename = 'addressFileRename',
  SignalOnCallScheduleModal = 'signalOnCallScheduleModal',
  SupportPolicyModal = 'supportPolicyModal',
  CopyText = 'copyText',
  CustomSignupLink = 'customSignupLink',
  PauseSubscription = 'pauseSubscription',
  ImportProposals = 'importProposals',
  ImportDtoolsProjects = 'importDtoolsProjects',
  CreateDraftWorkflowVersion = 'createDraftWorkflowVersion',
  ImportPortalioProjects = 'importPortalioProjects',
  ClientSegmentAddModal = 'clientSegmentAddModal',
  ClientSegmentEditModal = 'clientSegmentEditModal',
  ManageAssigneeGroup = 'manageAssigneeGroup',
  PublicApiKey = 'publicApiKey',
}

export const ModalComponents = {
  [Modals.AddComplimentaryPeriod]: AddComplimentaryPeriodModal,
  [Modals.AddConversationModal]: AddConversationModal,
  [Modals.AddClientModal]: AddClientModal,
  [Modals.AddPartnerDomainModal]: AddPartnerDomainModal,
  [Modals.CreatePartnerModal]: CreatePartnerModal,
  [Modals.AddPreSoldMembership]: AddPreSoldMembershipModal,
  [Modals.AddProject]: AddProjectModal,
  [Modals.AddSiteModal]: AddSiteModal,
  [Modals.UpdateClientToAddress]: UpdateClientToAddressModal,
  [Modals.ContactSupportModal]: ContactSupportModal,
  [Modals.CreateUser]: CreateUserModal,
  [Modals.MemberServicesFollowUp]: MemberServicesFollowUpModal,
  [Modals.MergeClientModal]: MergeClientModal,
  [Modals.MergeConversationModal]: MergeConversationModal,
  [Modals.MergeSiteModal]: MergeSiteModal,
  [Modals.ProposalLost]: ProposalLostModal,
  [Modals.ProposalWon]: ProposalWonModal,
  [Modals.ResentInviteWithPassword]: ResentInviteWithPasswordModal,
  [Modals.ScheduleHolidays]: ScheduleHolidayModal,
  [Modals.SendMembershipEmail]: SendMembershipEmailModal,
  [Modals.SnoozeConversationModal]: SnoozeConversationModal,
  [Modals.SubmitDay1]: SubmitDay1Modal,
  [Modals.UpdateAssigneeGroups]: UpdateUserAssigneeGroupsModal,
  [Modals.UpdateUser]: UpdateUserModal,
  [Modals.UpdateUserPermissions]: UpdateUserPermissionsModal,
  [Modals.QuickActionsModal]: QuickActionsModal,
  [Modals.MacrosModal]: MacrosModal,
  [Modals.UserSearchModal]: UserSearchModal,
  [Modals.AddCustomAttributeModal]: AddCustomAttributeModal,
  [Modals.ManageCustomAttributGroupModal]: ManageCustomAttributeGroupModal,
  [Modals.UpdateConversationToAddress]: UpdateConversationToAddressModal,
  [Modals.AiPrompts]: AiPromptModal,
  [Modals.ShortcutsModal]: ShortcutsModal,
  [Modals.AddressFileRename]: AddressFileRenameModal,
  [Modals.SignalOnCallScheduleModal]: SignalOnCallScheduleModal,
  [Modals.SupportPolicyModal]: SupportPolicyModal,
  [Modals.CopyText]: CopyTextModal,
  [Modals.CustomSignupLink]: CustomSignupLinkModal,
  [Modals.PauseSubscription]: PauseSubscriptionModal,
  [Modals.ImportProposals]: ImportProposalsModal,
  [Modals.ImportDtoolsProjects]: ImportDtoolsProjectModal,
  [Modals.CreateDraftWorkflowVersion]: CreateDraftWorkflowVersionModal,
  [Modals.ImportPortalioProjects]: ImportPortalioProjectModal,
  [Modals.ClientSegmentAddModal]: ClientSegmentAddModal,
  [Modals.ClientSegmentEditModal]: ClientSegmentEditModal,
  [Modals.ManageAssigneeGroup]: ManageAssigneeGroupModal,
  [Modals.PublicApiKey]: PublicApiKeyModal,
};

type ClientWithPartner = Partial<Omit<Client, 'partner'>> & {
  partner?: {
    id: number;
  } | null;
};

interface ModalsPropsMap {
  [Modals.SnoozeConversationModal]: {
    conversationId?: Conversation['conversationId'];
    conversationStatusId?: Conversation['conversationStatusId'];
    snoozeUntil?: Conversation['snoozeUntil'];
    partnerId?: Conversation['partnerId'];
    friendlyId?: Conversation['friendlyId'];
    subject?: Conversation['subject'];
  };
  [Modals.MergeConversationModal]: {
    conversation?: Conversation;
  };
  [Modals.MergeSiteModal]: {
    address: Address | null;
  };
  [Modals.ShortcutsModal]: null;
  [Modals.MergeClientModal]: {
    client?: MergeClient | null;
    filters?: ConditionInput[];
    limit?: number;
  };
  [Modals.ContactSupportModal]: null;
  [Modals.AddSiteModal]: {
    partnerId?: number;
    address?: unknown;
    client?: {
      partnerId?: number;
      id: string;
      name: string;
      email?: string;
    };
    filters?: ConditionInput[];
    limit?: number;
    sort?: SortingInput;
    offset?: number;
    onSuccessSubmit?: (result: Address) => void;
    onCloseModal?: () => void;
    prevFormValues?: unknown;
    closeOnSiteCreate?: boolean;
  } | void | null;
  [Modals.AddCustomAttributeModal]: {
    customAttribute?: CustomAttribute | null;
    customAttributeDefinition?: CustomAttributeDefinition | null;
    entity?: string | null;
  } | void | null;
  [Modals.SignalOnCallScheduleModal]: SignalOnCallScheduleModalProps;
  [Modals.AddClientModal]: {
    partnerId?: number;
    client?: ClientWithPartner | void | null;
    address?: Partial<Address> | void | null;
    participant?: Partial<ConversationParticipant> | void | null;
    onSuccessSubmit?: (result: NullableDeepPartial<ClientRaw>) => void;
    onCloseModal?: (client?: NullableDeepPartial<ClientRaw>) => void;
    filters?: ConditionInput[];
    limit?: number;
    sort?: SortingInput;
    conversation?: Conversation;
    clientId?: string;
  } | void | null;
  [Modals.AddConversationModal]: {
    prevFormValues?: unknown;
    requester: {
      id: string;
      label: string;
      participantEntity: ConversationParticipantEntity;
      email?: string;
      data: unknown;
    } | null;
    site?: { id: string; label: string } | null;
  };
  [Modals.MemberServicesFollowUp]: {
    addressId?: string;
    conversationId?: string;
  };
  [Modals.ManageCustomAttributGroupModal]: null;
  [Modals.SendMembershipEmail]: {
    address: Address | void | null;
  };
  [Modals.AddComplimentaryPeriod]: {
    address: Address | null;
    addCppToSchedule?: (
      params: ScheduleCreationParams,
    ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  } | void | null;
  [Modals.AddPreSoldMembership]: {
    addressId: string | null;
    onSubmit?: (data: AddPreSoldMembershipFormValues) => void;
    addPsmToSchedule?: (
      params: ScheduleCreationParams,
    ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  };
  [Modals.AddPartnerDomainModal]: {
    existingDomains: string[];
    onSubmit: (domain: string) => void;
  };
  [Modals.CreatePartnerModal]: {
    onReturnToSelector?: () => void;
  } | null;
  [Modals.UpdateUser]: {
    user: Partial<PlatformUserRaw>;
    onSubmit: (data: Partial<PlatformUserRaw>) => Promise<unknown> | void;
    disabledFields?: UpdateUserFormFields[];
  };
  [Modals.UpdateUserPermissions]: {
    user: Partial<PlatformUserRaw>;
    onSubmit: (data: ManageUserToUserGroupParams) => void;
  };
  [Modals.AddProject]: {
    address?: Address;
    filters?: ConditionInput[];
    limit?: number;
    sort?: SortingInput;
    offset?: number;
    prevFormValues?: AddProjectFormValues;
    onSuccessSubmit?: (result: Project) => void;
  } | void | null;
  [Modals.CreateUser]: {
    onSubmit: (data: Partial<PlatformUserRaw>) => Promise<unknown> | void;
    partnerId: number | null;
  };
  [Modals.SubmitDay1]: {
    address: Address | null;
    project: Project | null;
  } | void | null;
  [Modals.ProposalWon]: {
    address: Address | null;
    project: Project | null;
  } | void | null;
  [Modals.ProposalLost]: {
    address: Address | null;
    project: Project | null;
  } | void | null;
  [Modals.ResentInviteWithPassword]: {
    onSubmit: (userId: string, password: string) => void;
    userId: string;
  };
  [Modals.UpdateAssigneeGroups]: {
    user: Partial<PlatformUserRaw>;
    onSubmit: (data: ManageUserConversationAssigneeGroups) => void;
  };
  [Modals.ScheduleHolidays]:
    | {
        updateExisting: true;
        value: PartnerHolidayEntity;
      }
    | {
        updateExisting: false;
        value?: null | undefined;
      };
  [Modals.UpdateClientToAddress]: {
    client: Client;
    onSubmit?: (address: Address) => void;
  };
  [Modals.UpdateConversationToAddress]: {
    conversation: Conversation;
    address?: Address | null;
    onNewSiteAdded?: (newValue: Address) => void;
    onSelect?: (
      addressId: string,
      // think of refactor and reduce number of arguments
      extras: { siteToAdd?: Address; clientAddress?: Address; filters?: ConditionInput[] },
    ) => void;
  };
  [Modals.QuickActionsModal]: {
    actions: QuickAction[];
    isDisabled?: boolean;
  };
  [Modals.MacrosModal]: {
    conversation: Conversation;
    onMacroSelected: (macro: Macro, interpolatedMessage: string) => void;
  };
  [Modals.UserSearchModal]: {
    partnerId: number;
    onSelect: (participant: PreparedConversationParticipantOption | string) => void;
  };
  [Modals.AiPrompts]: {
    onSubmit?: (
      promptId?: string,
      promptName?: string,
      customPrompt?: string,
    ) => Promise<void>;
  };
  [Modals.AddressFileRename]: {
    fileName: string;
    onRename: (newName: string) => void;
  };
  [Modals.SupportPolicyModal]: {
    policy?: Partial<NormalizedSupportPolicy> | null;
    onSave?: (
      values: SupportPolicyFormValues,
      policy?: Partial<NormalizedSupportPolicy> | null,
    ) => void;
  } | null;
  [Modals.CopyText]: {
    title?: string;
    text: string;
    onSuccess?: () => void;
  };
  [Modals.CustomSignupLink]: {
    address: Address | void | null;
    addressSubscriptionId?: string | null;
  };
  [Modals.PauseSubscription]: {
    address: Address | null;
  };
  [Modals.ImportProposals]: {
    onItemsSelected: (
      items: { id: number | string; name: string }[],
      source: 'portalio' | 'dtools',
    ) => void;
  };
  [Modals.ImportDtoolsProjects]: null;
  [Modals.CreateDraftWorkflowVersion]: {
    workflowId?: string | null;
    initialDefinition?: string | null;
    workflows: Pick<WorkflowRaw, 'workflowId' | 'name'>[];
    refetchWorkflowVersions?: () => Promise<unknown>;
    onCreated?: (result: {
      workflowId: string;
      workflowVersionId: string;
      version: number;
    }) => void;
  };
  [Modals.ImportPortalioProjects]: null;
  [Modals.ClientSegmentAddModal]: {
    partnerId: number;
  };
  [Modals.ClientSegmentEditModal]: {
    segment: BrandWithSupport;
    sitesCount: number;
  };
  [Modals.ManageAssigneeGroup]:
    | { mode: 'create'; partnerId: number }
    | { mode: 'edit'; partnerId: number; group: ConversationAssigneeGroupManagement };
  [Modals.PublicApiKey]: PublicApiKeyModalProps;
}

export const ModalsByEntity: Record<
  EntityNames.Client | EntityNames.Site | EntityNames.Project | EntityNames.Conversation,
  Modals
> = {
  [EntityNames.Client]: Modals.AddClientModal,
  [EntityNames.Site]: Modals.AddSiteModal,
  [EntityNames.Project]: Modals.AddProject,
  [EntityNames.Conversation]: Modals.AddConversationModal,
};

type ModalPropsKey = string | symbol | number | ModalsPropsMap[keyof ModalsPropsMap];

interface ModalState {
  modal: Modals | null;
  setModal: (modalName: Modals | null) => void;
  modalProps: Record<Modals, Map<ModalPropsKey, ModalsPropsMap[keyof ModalsPropsMap]>>;
  setModalProps: <T extends Modals>(
    modalName: T,
    modalProps: ModalsPropsMap[T],
    modalPropsKey?: ModalPropsKey,
  ) => void;
  deleteModalProps: <T extends Modals>(modalName: T, modalPropsKey: ModalPropsKey) => void;
  closeCurrentModal: () => void;
}

const OPEN_PROPS_KEY = 'openProps';

export const useModalStore = create<ModalState>((set, get) => ({
  modal: null,
  modalProps: {} as ModalState['modalProps'],
  setModal: (modalName) => set({ modal: modalName }),
  setModalProps: (modalName, modalProps, modalPropsKey) => {
    const key = modalPropsKey || modalProps;
    if (key) {
      const modalPropsMap = get().modalProps?.[modalName];
      const updatedMap = new Map(modalPropsMap);
      updatedMap.set(key, modalProps);
      set({ modalProps: { ...get().modalProps, [modalName]: updatedMap } });
    }
  },
  deleteModalProps: (modalName, modalPropsKey) => {
    const modalPropsMap = get().modalProps?.[modalName];
    const updatedMap = new Map(modalPropsMap);
    updatedMap.delete(modalPropsKey);
    set({ modalProps: { ...get().modalProps, [modalName]: updatedMap } });
  },
  closeCurrentModal: () => {
    const currentModal = get().modal;
    if (currentModal) {
      set({ modal: null });
      get().deleteModalProps(currentModal, OPEN_PROPS_KEY);
    }
  },
}));

export const useModalState = <T extends Modals>(modalId: T) => {
  const { isOpened, modalProps: _modalProps } = useModalStore(
    useShallow((state) => ({
      isOpened: state.modal === modalId,
      modalProps: state.modalProps?.[modalId] || null,
    })),
  );

  const modalProps = useMemo(() => {
    if (!_modalProps) return null;

    if (_modalProps instanceof Map) {
      let result = {};
      _modalProps.forEach((value) => {
        result = { ...result, ...value };
      });
      return result as ModalsPropsMap[T];
    }

    return _modalProps;
  }, [_modalProps]);

  return useMemo(
    () => ({
      isOpened,
      modalProps,
    }),
    [isOpened, modalProps],
  );
};

export const useShowModals = <T extends Modals>(...args: T[]) => {
  const setModal = useModalStore((state) => state.setModal);
  const setModalProps = useModalStore((state) => state.setModalProps);

  return useMemo(
    () =>
      (args || []).map((modalId) => (props?: ModalsPropsMap[T]) => {
        if (props) {
          setModalProps(modalId, props, OPEN_PROPS_KEY);
        }
        setModal(modalId);
      }),
    // this hook has dynamic dependencies based on number
    // of arguments passed to it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...args, setModal, setModalProps],
  );
};

export const useModalMethods = <T extends Modals>(modalId: T) => {
  const setModal = useModalStore((state) => state.setModal);
  const _setModalProps = useModalStore((state) => state.setModalProps);
  const deleteModalProps = useModalStore((state) => state.deleteModalProps);
  const [showModal] = useShowModals(modalId);

  const closeModal = useCallback(() => {
    setModal(null);
    deleteModalProps(modalId, OPEN_PROPS_KEY);
  }, [setModal, deleteModalProps, modalId]);

  const setModalProps = useCallback(
    (modalProps: ModalsPropsMap[T], modalPropsKey?: string | symbol | number) => {
      _setModalProps(modalId, modalProps, modalPropsKey);
      return () => {
        deleteModalProps(modalId, modalPropsKey || modalProps);
      };
    },
    [modalId, _setModalProps, deleteModalProps],
  );

  return useMemo(
    () => ({
      showModal,
      setModalProps,
      closeModal,
    }),
    [closeModal, showModal, setModalProps],
  );
};

export const useCreateModalByEntity = (
  modalId:
    | EntityNames.Client
    | EntityNames.Site
    | EntityNames.Project
    | EntityNames.Conversation,
) => {
  const modal = useModalMethods(ModalsByEntity[modalId]);

  return modal;
};
